import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { catchError, of, take } from 'rxjs';
import {
  PaymentOverdueModalComponent,
  PaymentOverdueModalData,
} from '../../shared/modals/payment-overdue-modal/payment-overdue-modal.component';
import { getDaysOverdue } from '../../shared/utils/payment-due.util';
import { UserInfo } from '../../shared/models';
import { UserService } from '../http/user.service';
import { LoggedUserService } from './logged-user.service';
import { PaymentOverdueSoundService } from './payment-overdue-sound.service';

/** Delay between closing the notice and the next reminder. */
const REMINDER_MS = 30 * 60 * 1000;

/**
 * Shows the payment overdue notice for as long as the payment stays overdue: on
 * sign in, on reload, and every 30 minutes after it is dismissed.
 */
@Injectable({ providedIn: 'root' })
export class PaymentOverdueService {
  private dialogRef?: MatDialogRef<PaymentOverdueModalComponent>;
  private reminderId: any;

  constructor(
    private readonly dialog: MatDialog,
    private readonly loggedUserService: LoggedUserService,
    private readonly userService: UserService,
    private readonly sound: PaymentOverdueSoundService,
  ) {
    // LoggedUserService cannot inject this service (circular dependency), so the
    // sign-out cleanup is registered from here.
    this.loggedUserService.onSessionCleared = () => this.stop();
  }

  /**
   * Evaluate the due date and show the notice when the payment is overdue.
   *
   * `useCachedInfo` skips a redundant users/me call when the caller has just
   * loaded the user info (e.g. sign in).
   */
  start(useCachedInfo = false): void {
    if (!this.loggedUserService.loggedUser) return;

    if (useCachedInfo) {
      this.showIfOverdue();
      return;
    }

    this.refreshDueDateThenShow();
  }

  /** Close the notice and cancel the reminder (on sign out). */
  stop(): void {
    clearTimeout(this.reminderId);
    this.reminderId = undefined;
    this.sound.stop();
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }

  /**
   * Re-reads users/me so a due date changed by Billing takes effect without
   * requiring a new sign in. Falls back to the value already in localStorage
   * when the call fails.
   */
  private refreshDueDateThenShow(): void {
    this.userService
      .getLoggedUserInfo()
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe((info: UserInfo | null) => {
        if (info) {
          // isImpersonated/impersonatedBy aren't part of the users/me response.
          const cached = this.loggedUserService.loggedUserInfo;
          if (cached?.user?.isImpersonated && info.user.linkerUserId !== info.user.innovaUserId) {
            info.user.isImpersonated = true;
            info.user.impersonatedBy = cached.user.impersonatedBy;
          }
          this.loggedUserService.loggedUserInfo = info;
        }
        this.showIfOverdue();
      });
  }

  private showIfOverdue(): void {
    if (!this.loggedUserService.loggedUser) return;
    if (this.dialogRef) return;

    const daysOverdue = getDaysOverdue(this.loggedUserService.paymentDueDate);
    if (daysOverdue === null) return;

    const data: PaymentOverdueModalData = {
      daysOverdue,
      contactEmail: this.loggedUserService.paymentContactEmail,
    };
    this.dialogRef = this.dialog.open(PaymentOverdueModalComponent, {
      data,
      disableClose: true,
      autoFocus: false,
    });

    this.sound.play();

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
      this.sound.stop();
      this.scheduleReminder();
    });
  }

  private scheduleReminder(): void {
    clearTimeout(this.reminderId);
    this.reminderId = setTimeout(() => this.start(), REMINDER_MS);
  }
}
